import { useEffect, useRef } from 'react'

interface Props {
  amount?: number
  speed?: number
  scrollBoost?: number
}

export const StarfieldBackground = ({
  amount = 500,
  speed = 100,
  scrollBoost = 3,
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // === Вспомогательные классы ===
    class Vector2 {
      x: number
      y: number
      constructor(x: number, y: number) {
        this.x = x
        this.y = y
      }
    }
    class Vector3 {
      x: number
      y: number
      z: number
      constructor(x: number, y: number, z: number) {
        this.x = x
        this.y = y
        this.z = z
      }
    }
    function rand_range(min: number, max: number) {
      return min + Math.random() * (max - min)
    }
    function lerp(a: number, b: number, t: number) {
      return a + (b - a) * t
    }

    // === Starfield ===
    class jsStarfield {
      canvas: HTMLCanvasElement
      ctx: CanvasRenderingContext2D
      max_depth: number
      max_size: number
      speed: number
      targetSpeed: number
      baseSpeed: number
      amount: number
      follow_mouse: boolean
      method: string
      stars: Vector3[]
      origin: Vector2
      last_frame: number
      img_data: ImageData | undefined

      constructor(
        canvas: HTMLCanvasElement,
        amount: number,
        baseSpeed: number,
      ) {
        this.canvas = canvas
        this.ctx = canvas.getContext('2d')!
        this.max_depth = 1000
        this.max_size = 3
        this.speed = baseSpeed
        this.targetSpeed = baseSpeed
        this.baseSpeed = baseSpeed
        this.amount = amount
        this.follow_mouse = true
        this.method = 'rects'
        this.stars = []
        this.origin = new Vector2(0, 0)
        this.last_frame = 0

        this.resize()
        this.reset_origin()
        this.set_amount(this.amount)
      }

      resize() {
        this.canvas.width = this.canvas.offsetWidth
        this.canvas.height = this.canvas.offsetHeight
        this.img_data = this.ctx.getImageData(
          0,
          0,
          this.canvas.width,
          this.canvas.height,
        )
        this.reset_origin()
      }

      reset_origin() {
        this.origin.x = this.canvas.width / 2
        this.origin.y = this.canvas.height / 2
      }

      set_amount(amount: number) {
        this.amount = Math.floor(amount)
        if (this.amount < this.stars.length) {
          this.stars.length = this.amount
        } else {
          const more = this.amount - this.stars.length
          for (let i = 0; i < more; i++) {
            this.stars.push(
              new Vector3(
                rand_range(-this.canvas.width, this.canvas.width),
                rand_range(-this.canvas.height, this.canvas.height),
                rand_range(1, this.max_depth),
              ),
            )
          }
        }
      }

      update_stars(delta_time: number) {
        this.speed = lerp(this.speed, this.targetSpeed, delta_time * 5)
        const distance = this.speed * delta_time

        for (let i = 0; i < this.stars.length; i++) {
          const star = this.stars[i]
          star.z -= distance
          if (star.z <= 0) {
            star.x = rand_range(-this.canvas.width, this.canvas.width)
            star.y = rand_range(-this.canvas.height, this.canvas.height)
            star.z = this.max_depth
          }
        }
      }

      draw_rects() {
        this.ctx.fillStyle = 'gray'
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        for (let i = 0; i < this.stars.length; i++) {
          const star = this.stars[i]
          const k = 256 / star.z
          const x = star.x * k + this.origin.x
          const y = star.y * k + this.origin.y
          const size =
            ((this.max_depth - star.z) / this.max_depth) * this.max_size
          this.ctx.fillRect(x, y, size, size)
        }
      }

      update(time: number) {
        const delta_time = (time - this.last_frame) * 0.001
        this.update_stars(delta_time)
        this.draw_rects()
        this.last_frame = time
      }
    }

    const starfield = new jsStarfield(canvas, amount, speed)

    let running = true
    let lastScrollY = window.scrollY
    let isScrolling = false

    // === loop для проверки scrollY ===
    const scrollMonitor = () => {
      const currentY = window.scrollY
      if (currentY !== lastScrollY) {
        lastScrollY = currentY
        isScrolling = true
        starfield.targetSpeed = speed * scrollBoost
      } else {
        if (isScrolling) {
          isScrolling = false
          starfield.targetSpeed = speed
        }
      }
      if (running) {
        requestAnimationFrame(scrollMonitor)
      }
    }
    requestAnimationFrame(scrollMonitor)

    const animate = (time: number) => {
      if (!running) return
      starfield.update(time)
      requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)

    const handleResize = () => starfield.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      running = false
      window.removeEventListener('resize', handleResize)
    }
  }, [amount, speed, scrollBoost])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        background: 'transparent',
        pointerEvents: 'none',
      }}
    />
  )
}
